import type React from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { DomainsFilter } from "pro-features/components/DomainsFilter/DomainsFilter";
import type { IDomains } from "pro-features/components/DomainsFilter/Types";
import { EAdsTabs } from "app/components/React/PlaResearch/type";
import type { ActivationObjectSchema } from "app/pages/activation/types/api";
import fetchService from "app/common/services/salesFetchService";
import { SHOPPING, TEXT } from "app/common/keyword-research/website-search-ads/searchAdsTableConsts";
import DurationService from "app/services/DurationService";
import { requestParams } from "app/services/fetchService";

import { keywordGroupsTransforms } from "app/services/keyword-groups";
import { Flex } from "@similarweb/ui-components/foundations";

const FiltersContainers = styled(Flex)`
    margin: 16px 16px 16px 0;
`;

const ChipdownItem = styled.div`
    flex-grow: 0;
    margin-right: 8px;
`;
const FiltersContainersWrapper = styled(FiltersContainers)`
    margin-right: 0;
    ${ChipdownItem} {
        margin-right: 0;
    }
`;

interface IFiltersSectionProps {
    domainsAPI: string;
    activationSets: ActivationObjectSchema[];
    sortConfig: { sort: string; asc: boolean };
    requestBody: any;
}
export const FiltersSection: React.FC<IFiltersSectionProps> = ({
    domainsAPI,
    activationSets,
    sortConfig,
    requestBody,
}) => {
    const { params } = useCurrentStateAndParams();
    const {
        country,
        duration,
        webSource,
        key,
        selectedPageTab,
        keyword,
        domains,
        multiIncludeExcludeUrl,
        multiIncludeExcludeTitle,
        selectedCategory,
    } = params;
    const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;
    const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(keyword);

    const apiParams = {
        country: country,
        webSource,
        to,
        from,
        isWindow,
        keys: isKeywordGroup ? keywordGroupsTransforms.getGroupIdFromUrlParam(keyword) : keyword,
        sort: sortConfig.sort,
        asc: sortConfig.asc,
        adType: selectedPageTab === EAdsTabs[EAdsTabs.Text] ? TEXT : SHOPPING,
        category: selectedCategory,
    };
    const paramsAsString = requestParams(apiParams);

    const { data, isLoading, isError } = useQuery({
        queryKey: [
            domainsAPI,
            duration,
            key,
            country,
            selectedPageTab,
            sortConfig.asc,
            sortConfig.sort,
            webSource,
            to,
            from,
            isWindow,
            keyword,
            multiIncludeExcludeUrl,
            multiIncludeExcludeTitle,
            domains,
            selectedCategory,
        ],
        queryFn: async () => fetchService.post(`${domainsAPI}?${paramsAsString}`, requestBody),
        select: (response: IDomains[]) => {
            return response;
        },
    });

    return (
        <FiltersContainersWrapper alignItems="center">
            <ChipdownItem data-automation="domains-filter-wrapper">
                <DomainsFilter
                    domainsFilterData={data}
                    activationSets={activationSets}
                />
            </ChipdownItem>
        </FiltersContainersWrapper>
    );
};
